import {
    DecisionDataStore,
    UnderReview,
    Approved,
    Declined,
    PrequalificationDecision,
} from '@servicetitan/financing-decision-shared';
import {
    SetupDataStore,
    useProviderConfig,
    WorkflowStage,
    WorkflowStore,
} from '@servicetitan/financing-workflow-shared';
import { SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import { PrequalificationStatus } from '@servicetitan/financing-api-client/src/prequals-api';
import { ErrorBoundary } from '@servicetitan/financing-utils-shared';
import {
    WorkflowLayoutsStore,
    ExternalApplicationWorkflowStore,
    SubmitLoan,
    DecisionWorkflow,
    SecondLookDecisionWorkflow,
    ResetWorkflow,
    CustomerAuthStep,
    PrequalsSelectPlanStep,
    ReviewingWorkflow,
    WellsFargoReviewingWorkflow,
    ApplicationWithCoApplicationWorkflow,
} from '@servicetitan/financing-application-workflow-shared';
import { PrequalificationWorkflow } from './PrequalificationWorkflow';
import { PrequalificationReviewingWorkflow } from './PrequalificationReviewingWorkflow';

interface WorkflowRendererProps {
    workflowStage: WorkflowStage;
    canChangeProvider?: boolean;
}

export const WorkflowRenderer = ({ workflowStage }: WorkflowRendererProps) => {
    const { decisionData } = DecisionDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { prequalDecisionData } =
        PrequalificationDecision.PrequalificationDecisionDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { DefaultLayout } = WorkflowLayoutsStore.useStore();
    const { ExternalApplicationWorkflow } = ExternalApplicationWorkflowStore.useStore();
    const { useCustomerAuthFlow, hasMailingAddress } = useProviderConfig();
    const hasTransitionedToHardApp = setupData?.hasTransitionedToHardApp;
    const isApplyingForPrequalification = setupData?.isApplyingForPrequalification;

    // Check if this is a GoodLeap prequalification flow
    const isGoodLeapPrequalification =
        isApplyingForPrequalification && setupData?.provider?.toLowerCase() === 'goodleap';

    const getApplicationWorkflowStage = () => {
        // Providers without separate mailing address step use the compact workflow
        if (Boolean(isGoodLeapPrequalification) || !hasMailingAddress) {
            return 'application-with-co-application';
        }
        return 'application';
    };

    const onPlanSelected = () => {
        if (useCustomerAuthFlow && !hasTransitionedToHardApp) {
            setWorkflowStage('customer-auth');
        } else if (setupData?.provider?.toLowerCase() === 'greensky') {
            setWorkflowStage('application');
        } else {
            setWorkflowStage(getApplicationWorkflowStage());
        }
    };

    switch (workflowStage) {
        case 'external-application':
            return <ExternalApplicationWorkflow />;
        case 'customer-auth':
            return (
                <DefaultLayout>
                    <CustomerAuthStep onCompleted={() => setWorkflowStage('application')} />
                </DefaultLayout>
            );
        case 'plan-selection':
            return (
                <DefaultLayout>
                    <PrequalsSelectPlanStep onPlanSelected={onPlanSelected} />
                </DefaultLayout>
            );
        case 'application':
            return <PrequalificationWorkflow />;
        case 'application-with-co-application':
            return <ApplicationWithCoApplicationWorkflow />;
        case 'submitting':
            return (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError="submitting a loan"
                        onRetry={() => setWorkflowStage(getApplicationWorkflowStage())}
                    >
                        <SubmitLoan />
                    </ErrorBoundary>
                </DefaultLayout>
            );
        case 'decision':
            return prequalDecisionData?.status === PrequalificationStatus.Approved ? (
                <DefaultLayout>
                    <PrequalificationDecision.Approved />
                </DefaultLayout>
            ) : prequalDecisionData?.status === PrequalificationStatus.Declined ? (
                <DefaultLayout>
                    <PrequalificationDecision.Declined />
                </DefaultLayout>
            ) : prequalDecisionData?.status === PrequalificationStatus.Review ||
              decisionData.decision === SubmitApplicationDecision.Review ? (
                <DefaultLayout>
                    <UnderReview />
                </DefaultLayout>
            ) : decisionData.decision === SubmitApplicationDecision.Approved ? (
                <DefaultLayout>
                    <Approved />
                </DefaultLayout>
            ) : decisionData.decision === SubmitApplicationDecision.Declined ? (
                <DefaultLayout>
                    <Declined />
                </DefaultLayout>
            ) : (
                // Fallback to standard decision workflow for other cases
                <DecisionWorkflow />
            );
        case 'second-look-decision':
            return (
                <DefaultLayout>
                    <ErrorBoundary actionCausingError="loading qualification decision">
                        <SecondLookDecisionWorkflow />
                    </ErrorBoundary>
                </DefaultLayout>
            );
        case 'reviewing': {
            const isWellsFargoProvider = setupData?.provider?.toLowerCase() === 'wellsfargo';
            return (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError={
                            isWellsFargoProvider || isApplyingForPrequalification
                                ? 'reviewing your pre-qualification'
                                : 'reviewing your application'
                        }
                        onRetry={() => setWorkflowStage(getApplicationWorkflowStage())}
                    >
                        {isWellsFargoProvider ? (
                            <WellsFargoReviewingWorkflow />
                        ) : isApplyingForPrequalification ? (
                            <PrequalificationReviewingWorkflow />
                        ) : (
                            <ReviewingWorkflow />
                        )}
                    </ErrorBoundary>
                </DefaultLayout>
            );
        }
        case 'reset':
            return <ResetWorkflow />;
        default:
            return null;
    }
};
